import { useEffect, useState } from 'react';
import { getPromoSpots } from '@/lib/spotsCounter';

export function PromoSpotsDisplay({ variant = 'default' }) {
  const [spots, setSpots] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSpots = async () => {
      const data = await getPromoSpots();
      setSpots(data);
      setLoading(false);
    };

    loadSpots();

    // Refresh every 5 minutes
    const interval = setInterval(loadSpots, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading || !spots) {
    // Return placeholder with same dimensions to prevent layout shift
    if (variant === 'hero') {
      return <span className="text-[10px] opacity-80">Save $50/mo forever — Limited spots</span>;
    }
    if (variant === 'badge') {
      return <span className="text-xs font-medium text-red-400">Limited spots remaining</span>;
    }
    return <span className="text-sm text-muted-foreground">Limited availability</span>;
  }

  const { remainingSpots, isLastChance, isSecondRound, message, urgencyLevel } = spots;

  // Different display variants
  if (variant === 'hero') {
    return (
      <span className={`text-[10px] opacity-80 ${urgencyLevel === 'critical' ? 'text-red-400 font-bold' : ''}`}>
        Save $50/mo forever — {remainingSpots} spots left
      </span>
    );
  }

  if (variant === 'badge') {
    const colorClass = urgencyLevel === 'critical' ? 'text-red-500' :
                       urgencyLevel === 'high' ? 'text-red-400' :
                       urgencyLevel === 'medium' ? 'text-orange-400' :
                       'text-lime-400';

    return (
      <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
        urgencyLevel === 'critical' ? 'bg-red-500/20 border-red-500/30' :
        urgencyLevel === 'high' ? 'bg-red-500/10 border-red-500/20' :
        'bg-lime-500/10 border-lime-500/20'
      } border`}>
        <span className="relative flex h-2 w-2">
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${
            urgencyLevel === 'critical' ? 'bg-red-400' : 'bg-lime-400'
          } opacity-75`}></span>
          <span className={`relative inline-flex rounded-full h-2 w-2 ${
            urgencyLevel === 'critical' ? 'bg-red-500' : 'bg-lime-500'
          }`}></span>
        </span>
        <p className={`text-xs font-medium ${colorClass}`}>
          {message}
        </p>
      </div>
    );
  }

  if (variant === 'pricing') {
    if (remainingSpots === 0) {
      return (
        <p className="text-xs text-lime-400 animate-pulse">
          {isSecondRound ? 'All spots claimed!' : 'Second chance batch coming soon!'}
        </p>
      );
    }

    return (
      <>
        <p className={`text-xs ${
          urgencyLevel === 'critical' ? 'text-red-500 font-bold' : 'text-red-400'
        }`}>
          {isSecondRound ? `🔥 SECOND CHANCE: ` : ''}
          Only {remainingSpots} spots remaining at this price
        </p>
        {urgencyLevel === 'critical' && (
          <p className="text-[10px] text-red-400/80 animate-pulse">
            Once these are gone, price goes to $99/mo
          </p>
        )}
      </>
    );
  }

  if (variant === 'form') {
    return (
      <p className="text-sm text-muted-foreground">
        {isSecondRound && (
          <span className="text-lime-400 font-semibold">Second chance! </span>
        )}
        {message}
      </p>
    );
  }

  // Default variant
  return <span className="text-sm text-muted-foreground">{message}</span>;
}

// Inline version for CTAs
export function PromoSpotsInline() {
  const [spots, setSpots] = useState(null);

  useEffect(() => {
    getPromoSpots().then(setSpots);
  }, []);

  if (!spots) return null;

  const { remainingSpots, urgencyLevel } = spots;

  if (remainingSpots === 0) {
    return <span className="text-lime-400">New batch soon</span>;
  }

  return (
    <span className={
      urgencyLevel === 'critical' ? 'text-red-400 font-bold' :
      urgencyLevel === 'high' ? 'text-orange-400' :
      'text-muted-foreground'
    }>
      {remainingSpots} left
    </span>
  );
}